import { useEffect, useState } from "react";
import { MessageSquare, Send, Trash2 } from "lucide-react";
import { Modal } from "antd";

const identity = (value) =>
  value && !["undefined", "null"].includes(value) ? value : "";
export const readNotes = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return (Array.isArray(saved) ? saved : saved ? [saved] : []).filter(
      (note) =>
        note &&
        typeof note.text === "string" &&
        typeof note.author === "string" &&
        note.savedAt &&
        !Number.isNaN(new Date(note.savedAt).getTime()),
    );
  } catch {
    return [];
  }
};
export default function InvoiceFollowUp({ bill }) {
  const key = `invoice-follow-up:${localStorage.getItem("userId") || "unknown"}:${bill._id}`;
  const [notes, setNotes] = useState(() => readNotes(key));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    setNotes(readNotes(key));
    setDraft("");
    setEditing(false);
    setError("");
  }, [key, bill.notesRelance]);
  const cancel = () => {
    setDraft("");
    setEditing(false);
    setError("");
  };
  const save = (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    const author =
      [
        identity(localStorage.getItem("firstname")),
        identity(localStorage.getItem("lastname")),
      ]
        .filter(Boolean)
        .join(" ") || identity(localStorage.getItem("userEmail"));
    if (!author || !bill._id) {
      setError("Impossible d’identifier l’auteur ou la facture.");
      return;
    }
    try {
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text,
        author,
        savedAt: new Date().toISOString(),
      };
      const history = [entry, ...readNotes(key)];
      localStorage.setItem(key, JSON.stringify(history));
      setNotes(history);
      setDraft("");
      setError("");
    } catch {
      setError("Impossible d’enregistrer le commentaire sur cet appareil.");
    }
  };
  const remove = (index) => {
    try {
      const history = notes.filter((_, current) => current !== index);
      localStorage.setItem(key, JSON.stringify(history));
      setNotes(history);
      setError("");
    } catch {
      setError("Impossible de supprimer le commentaire.");
    }
  };
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        onClick={() => {
          setNotes(readNotes(key));
          setDraft("");
          setError("");
          setEditing(true);
        }}
        aria-label="Ajouter un commentaire"
        title="Commentaires de la facture"
        className="inline-flex items-center justify-center rounded-lg p-2 text-blue-700 hover:bg-blue-50"
      >
        <MessageSquare size={18} />
      </button>
      <Modal
        open={editing}
        onCancel={cancel}
        footer={null}
        centered
        width={760}
        title={`Commentaires — Facture ${bill.numeroFacture || bill.IdFacture || "—"}${bill.client?.companyName ? ` · ${bill.client.companyName}` : ""}`}
      >
        <p className="mb-5 mt-2 text-sm leading-relaxed text-slate-500">
          Notes internes pour le suivi des impayés : relances, promesses de
          paiement, litiges et contacts.
        </p>
        <div className="max-h-[40vh] space-y-3 overflow-y-auto">
          {notes.map((note, index) => (
            <article
              key={note.id || `${note.savedAt}-${index}`}
              className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3"
            >
              <div className="mb-1 flex items-center justify-between gap-3">
                <strong className="text-sm text-slate-700">
                  {note.author}
                </strong>
                <div className="flex shrink-0 items-center gap-2">
                  <time
                    dateTime={note.savedAt}
                    className="text-xs text-slate-400"
                  >
                    {new Date(note.savedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })}
                  </time>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    aria-label={`Supprimer le commentaire de ${note.author}`}
                    className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">
                {note.text}
              </p>
            </article>
          ))}
          {bill.notesRelance && (
            <article className="rounded-xl border border-slate-200 px-4 py-3">
              <p className="mb-1 text-xs text-slate-400">
                Commentaire existant de la facture
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
                {bill.notesRelance}
              </p>
            </article>
          )}
          {!notes.length && !bill.notesRelance && (
            <p className="py-4 text-center text-sm text-slate-400">
              Aucun commentaire pour le moment.
            </p>
          )}
        </div>
        <form
          onSubmit={save}
          className="mt-5 space-y-3 border-t border-slate-100 pt-4"
        >
          <textarea
            aria-label="Commentaire de suivi"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={1000}
            rows={2}
            required
            placeholder="Ajouter un commentaire (ex. : Relancé par téléphone, paiement promis le 15)"
            className="w-full resize-y rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              Enregistrés sur cet appareil · partage avec l’équipe à connecter.
            </p>
            <button
              type="submit"
              disabled={!draft.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-40"
            >
              <Send size={16} />
              Ajouter
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
